// =============================================================================
// @tnb/expected-boxes — Single source of truth for finished-box counts (WCO-2469)
//
// THE INVARIANT (physically accurate, packer-correct):
//   per sub-session:  expected_boxes = FLOOR(target_output_weight / unit_weight)
//   per session:      total_boxes    = SUM over non-cancelled sub-sessions of FLOOR(sub_tow / unit_weight)
//
// Why FLOOR-per-sub then SUM (NOT FLOOR-of-sum):
//   A station can only pack WHOLE finished boxes. 3.9 boxes of output → 3 packed boxes + an un-packable
//   remainder. When a session is split across stations, each station's remainder is physically separate and
//   CANNOT combine into another box. So the session total is the sum of what each station independently packs:
//     FLOOR(3.9) + FLOOR(3.1) = 3 + 3 = 6   (correct)   ── NOT ──   FLOOR(3.9 + 3.1) = FLOOR(7.0) = 7 (wrong)
//   The old code mixed FLOOR(sub) (operator) with ROUND(total_boxes * share) (supervisor) and FLOOR(SUM)
//   (stored total_boxes), producing the 3-vs-4-vs-5 disagreement. This package makes every path use the one
//   formula so they can never diverge again.
//
// `target_output_weight` already has yield applied (initial_material_weight * yield_factor), so no yield math
// here — this is purely the weight → whole-box count.
// =============================================================================

'use strict';

/**
 * SQL expression for a SINGLE sub-session's expected finished-box count.
 * Always FLOOR(target_output_weight / unit_weight); the total_boxes column is only a last-resort fallback
 * for the degenerate case where the product's unit_weight is unknown (0/NULL).
 *
 * @param {string} [twCol='pss.target_output_weight'] - SQL expr for the sub-session's yield-adjusted output weight
 * @param {string} [unitCol='i.unit_weight']          - SQL expr for the finished product's per-box unit weight
 * @param {string} [totalBoxesCol]                     - optional SQL expr for a stored total_boxes fallback
 * @returns {string} SQL CASE fragment evaluating to an integer box count
 *
 * Usage:
 *   `SELECT ${EXPECTED_BOXES_SQL('pss.target_output_weight', 'i.unit_weight')} AS expected_boxes ...`
 */
function EXPECTED_BOXES_SQL(twCol, unitCol, totalBoxesCol) {
  twCol = twCol || 'pss.target_output_weight';
  unitCol = unitCol || 'i.unit_weight';
  const fallback = totalBoxesCol
    ? `WHEN COALESCE(${totalBoxesCol}, 0) > 0 THEN ${totalBoxesCol}\n          `
    : '';
  return `CASE
          WHEN COALESCE(${unitCol}, 0) > 0 AND ${twCol} > 0
          THEN FLOOR(${twCol} / ${unitCol})
          ${fallback}ELSE 0
        END`;
}

/**
 * SQL scalar SUBQUERY for a PARENT session's total finished-box count = SUM of each non-cancelled
 * sub-session's FLOOR(target_output_weight / unit_weight). This is the SUM(FLOOR), NOT FLOOR(SUM).
 *
 * The subquery joins each sub-session to ics.inventory for the product's unit_weight, so it works whether or
 * not the caller already has the unit_weight in scope.
 *
 * @param {string} sessionNumberExpr - SQL expr for the parent session_number to sum over (e.g. 'ps.session_number')
 * @returns {string} SQL scalar subquery (wrap-free) evaluating to an integer
 *
 * Usage (as a computed column or an UPDATE SET value):
 *   `SET total_boxes = ${PARENT_TOTAL_BOXES_SQL('ps.session_number')}`
 *   `SELECT ${PARENT_TOTAL_BOXES_SQL('ps.session_number')} AS total_boxes ...`
 */
function PARENT_TOTAL_BOXES_SQL(sessionNumberExpr) {
  sessionNumberExpr = sessionNumberExpr || 'ps.session_number';
  return `(
    SELECT COALESCE(SUM(
             CASE WHEN COALESCE(eb_inv.unit_weight, 0) > 0 AND eb_sub.target_output_weight > 0
                  THEN FLOOR(eb_sub.target_output_weight / eb_inv.unit_weight)
                  ELSE 0 END
           ), 0)::int
    FROM ics.hte_production_sub_session eb_sub
    JOIN ics.hte_production_session eb_ps ON eb_ps.session_number = eb_sub.session_number
    LEFT JOIN ics.inventory eb_inv ON eb_inv.item_number = eb_ps.product_sku
    WHERE eb_sub.session_number = ${sessionNumberExpr}
      AND eb_sub.status <> 'cancelled'
  )`;
}

/**
 * JS helper — the same per-sub-session formula for non-SQL paths.
 * @param {{ targetOutputWeight?: number|string|null, unitWeight?: number|string|null, totalBoxes?: number|string|null }} row
 * @returns {number} integer box count
 */
function expectedBoxes(row) {
  const tw = Number(row && row.targetOutputWeight) || 0;
  const unit = Number(row && row.unitWeight) || 0;
  if (unit > 0 && tw > 0) return Math.floor(tw / unit);
  const total = Number(row && row.totalBoxes) || 0;
  return total > 0 ? total : 0;
}

module.exports = {
  EXPECTED_BOXES_SQL,
  PARENT_TOTAL_BOXES_SQL,
  expectedBoxes,
};
